// src/components/admin/AdminDashboardPage.tsx
// Admin landing page: three blocks — Create New Condo, View Condos Created, Incomplete Condos

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import BackgroundShapes from '../common/BackgroundShapes';

const AdminDashboardPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const handleCreateCondo = () => {
    navigate('/admin/condos');
  };

  const handleViewCondos = () => {
    navigate('/admin/condos/list');
  };

  const handleViewDrafts = () => {
    navigate('/admin/condos/drafts');
  };

  const handleReviewSubmissions = () => {
    navigate('/admin/condos/pending');
  };

  const handleViewStatus = () => {
    navigate('/admin/status');
  };

  const handleViewBroadcasts = () => {
    navigate('/admin/broadcasts');
  };

  const handleViewHealth = () => {
    navigate('/admin/health');
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-coffee mb-2">
              Admin Dashboard
            </h1>
            <p className="text-coffee opacity-80">
              Manage condo listings
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm"
          >
            Sign Out
          </button>
        </div>

        {/* Dashboard Content — three blocks */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Block 1: Create New Condo */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-coffee mb-4">Create New Condo</h2>
            <div className="text-center">
              <p className="text-coffee opacity-80 text-sm mb-4">
                Add a new condo listing with details, images, and unit types.
              </p>
              <button
                onClick={handleCreateCondo}
                className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
              >
                Create Condo
              </button>
            </div>
          </div>

          {/* Block 2: Condos (Published + Drafts merged) */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-[4px_4px_0px_#594A42]">
            <h2 className="text-xl font-bold text-coffee mb-4">Condos</h2>
            <p className="text-coffee opacity-80 text-sm mb-4">
              View published listings or resume editing saved drafts.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleViewCondos}
                className="flex-1 bg-terracotta text-white px-3 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm text-sm"
              >
                Published
              </button>
              <button
                onClick={handleViewDrafts}
                className="flex-1 bg-white border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm text-sm font-medium"
              >
                Drafts
              </button>
            </div>
          </div>

          {/* Block 4: Review Submissions */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-coffee mb-4">Review Submissions</h2>
            <div className="text-center">
              <p className="text-coffee opacity-80 text-sm mb-4">
                Review condo listings submitted by users for publication.
              </p>
              <button
                onClick={handleReviewSubmissions}
                className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
              >
                Review Submissions
              </button>
            </div>
          </div>

          {/* Block 5: Status Board */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-coffee mb-4">Status Board</h2>
            <div className="text-center">
              <p className="text-coffee opacity-80 text-sm mb-4">
                Engineering tasks &amp; marketing campaigns
              </p>
              <button
                onClick={handleViewStatus}
                className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
              >
                View Status
              </button>
            </div>
          </div>

          {/* Block 6: Broadcasts */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-coffee mb-4">Broadcasts</h2>
            <div className="text-center">
              <p className="text-coffee opacity-80 text-sm mb-4">
                Compose and send email broadcasts to users.
              </p>
              <button
                onClick={handleViewBroadcasts}
                className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
              >
                Broadcasts
              </button>
            </div>
          </div>

          {/* Block 7: User Health */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-coffee mb-4">User Health</h2>
            <div className="text-center">
              <p className="text-coffee opacity-80 text-sm mb-4">
                Enrollment, login activity, welcome &amp; suggestion email status.
              </p>
              <button
                onClick={handleViewHealth}
                className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm"
              >
                View Health
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
